import {
  AudienceCommunicationPreferenceTargetingAttributeFragment,
  AudiencePointBalanceTargetingAttributeFragment,
  AudienceTimeSinceLastVisitTargetingAttribute,
  AudienceTimeSinceLastVisitTargetingAttributeFragment,
  AudienceTimeSinceMembershipAcquisitionTargetingAttributeFragment,
} from '@occc/graphql';
import { pluralize } from '@occc-app/lib/strings';

import {
  membershipCreatedOperatorToTitleMap,
  optInStatusToTitleMap,
  pointBalanceOperatorToTitleMap,
  targetToTitleMap,
} from './constants';

type CommunicationOptInCriterion =
  AudienceCommunicationPreferenceTargetingAttributeFragment['criteria'][number];

const timeSinceLastVisitOperatorToTitleMap: Record<
  AudienceTimeSinceLastVisitTargetingAttribute['operator'],
  string
> = {
  IS_EQUAL_TO: '',
  IS_GREATER_THAN: 'More than',
  IS_GREATER_THAN_OR_EQUAL_TO: 'More than or',
  IS_LESS_THAN: 'Less than',
  IS_LESS_THAN_OR_EQUAL_TO: 'Less than or',
};

const timePeriodToTitleMap: Record<AudienceTimeSinceLastVisitTargetingAttribute['period'], string> =
  {
    DAY: 'day',
    WEEK: 'week',
    MONTH: 'month',
  };

export const getHumanReadableLastVisitTargetingAttribute = ({
  amount,
  period,
  timeSinceLastVisitOperator,
}: Omit<AudienceTimeSinceLastVisitTargetingAttributeFragment, '__typename'>): string => {
  const comparatorTitle = timeSinceLastVisitOperatorToTitleMap[timeSinceLastVisitOperator];
  const timePeriodTitle = timePeriodToTitleMap[period];
  return `${comparatorTitle}${comparatorTitle ? ' ' : ''}${amount} ${pluralize(
    amount,
    timePeriodTitle,
  )} ago`;
};

export const getHumanReadablePointBalanceTargetingAttribute = ({
  target,
  minAmount,
  maxAmount,
  pointBalanceAmount,
  pointBalanceOperator,
}: AudiencePointBalanceTargetingAttributeFragment): string => {
  const targetTitle = targetToTitleMap[target];

  if (!pointBalanceOperator && typeof minAmount === 'number' && typeof maxAmount === 'number') {
    return `Between (and including) ${minAmount}-${maxAmount} points ${targetTitle}`;
  }

  if (!pointBalanceOperator || typeof pointBalanceAmount !== 'number') {
    return '';
  }

  const operatorTitle = pointBalanceOperatorToTitleMap[pointBalanceOperator];

  return `${operatorTitle}${operatorTitle ? ' ' : ''}${pointBalanceAmount} ${pluralize(
    pointBalanceAmount,
    'point',
  )} ${targetTitle}`;
};

export const getHumanReadableMembershipCreatedTargetingAttribute = ({
  membershipCreatedAmount,
  membershipCreatedOperator,
}: AudienceTimeSinceMembershipAcquisitionTargetingAttributeFragment): string => {
  const operatorTitle = membershipCreatedOperatorToTitleMap[membershipCreatedOperator];
  return `${operatorTitle}${operatorTitle ? ' ' : ''}${membershipCreatedAmount} ${pluralize(
    membershipCreatedAmount,
    'day',
  )} ago`;
};

export const getHumanReadableCommunicationOptInTargetingAttribute = (
  { status }: CommunicationOptInCriterion,
  subBrandName?: string,
): string => {
  const statusTitle = optInStatusToTitleMap[status];
  return subBrandName ? `${statusTitle} for ${subBrandName}` : statusTitle;
};
